import { forwardRef, useState, useEffect, useCallback, useId, useImperativeHandle, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useOrganization } from '../contexts/OrganizationContext'
import { kambiApi, commandCenterApi } from '../services/api'
import { parseOutcomeId } from '../utils/odds'
import { ROLE_HIERARCHY, ROLES } from '../constants/roles'
import { formatStatusLabel } from './common'
import CodeBlock from './common/data/CodeBlock'
import styles from './KambiOutcomeSelector.module.css'

const OUTCOME_NOT_FOUND_ERROR_PATTERN = /no bet offers? found|bet (?:offer )?not found|outcome[^.\n]*not found|(?:api returned )?status(?: code)? 404/
const STALE_PRICE_ERROR_PATTERN = /stale[^.\n]*price|price[^.\n]*stale/
const UPSTREAM_UNAVAILABLE_ERROR_PATTERN = /unavailable|timed? out|network|failed to fetch/

const OFFERING_LABELS = {
  default: 'Default Routing',
}
const EMPTY_OFFERINGS = []

function getOfferingLabel(offeringId) {
  return OFFERING_LABELS[offeringId] || offeringId
}

function validationDetailText(value) {
  if (typeof value === 'string') return value.trim()
  if (value == null) return ''
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

function validationFailure(error) {
  const data = error?.response?.data
  const detailParts = [
    validationDetailText(data?.error),
    ...(Array.isArray(data?.details) ? data.details.map(validationDetailText) : []),
    validationDetailText(error?.message),
  ].filter(Boolean)
  const technicalDetails = [...new Set(detailParts)].join('\n')
  const normalized = technicalDetails.toLowerCase()

  if (OUTCOME_NOT_FOUND_ERROR_PATTERN.test(normalized)) {
    return {
      title: 'Bet not found',
      description: 'Check the outcome ID, change Regulations, or search for the market.',
      technicalDetails,
      tone: 'notFound',
    }
  }
  if (STALE_PRICE_ERROR_PATTERN.test(normalized)) {
    return {
      title: 'Bet price changed',
      description: 'Search for the market again to attach its current price.',
      technicalDetails,
      tone: 'warning',
    }
  }
  if (UPSTREAM_UNAVAILABLE_ERROR_PATTERN.test(normalized)) {
    return {
      title: 'Bet validation is unavailable',
      description: 'Try again in a moment. The attached outcome ID has been kept.',
      technicalDetails,
      tone: 'warning',
    }
  }
  return {
    title: 'Bet could not be checked',
    description: 'Try again or search for the market instead.',
    technicalDetails,
    tone: 'warning',
  }
}

function AvailabilitySummary({ availability, targetOfferings, allOfferings }) {
  if (!availability) return null

  const relevantOfferings = targetOfferings.length > 0 ? targetOfferings : allOfferings
  const total = relevantOfferings.length
  if (total === 0) return null

  const available = relevantOfferings.filter((offeringId) => availability[offeringId] === true)
  const unavailable = relevantOfferings.filter((offeringId) => availability[offeringId] !== true)
  const availableCount = available.length

  if (availableCount === total) {
    return <span className={styles.availabilityGood}>All {total} regulations available</span>
  }

  if (availableCount === 0) {
    return <span className={styles.availabilityBad}>Unavailable under selected regulations</span>
  }

  return (
    <span className={styles.availabilityWarn}>
      {availableCount}/{total} regulations available
      {unavailable.length > 0 ? ` · Missing ${unavailable.map(getOfferingLabel).join(', ')}` : ''}
    </span>
  )
}

const KambiOutcomeSelector = forwardRef(function KambiOutcomeSelector({
  outcomeIds,
  targetOfferings,
  allOfferings: allOfferingsProp,
  providerId,
  destinations,
  onOutcomeIdsChange,
  onValidationResult,
  hideValidationPreview = false,
  outcomeEntryDisclosure = false,
  hideOutcomeEntry = false,
}, ref) {
  const { currentOrg } = useOrganization()
  // Command Center passes `destinations` (one entry per selected organization,
  // each with its own regulation IDs) and validates via the source-free V2
  // route. Without `destinations` this falls back to the legacy single-org
  // tool, which still resolves its org from OrganizationContext.
  const isDestinationScoped = Array.isArray(destinations)
  const orgSlug = isDestinationScoped ? null : currentOrg?.slug
  // Raw provider diagnostics (offering codes, upstream status lines) help
  // Wager staff debug; book users get the plain-language summary only.
  // Command Center is Wager-only, so its destination-scoped mode always shows them.
  const showTechnicalDetails = isDestinationScoped
    || (ROLE_HIERARCHY[currentOrg?.role] || 0) >= ROLE_HIERARCHY[ROLES.INTERNAL_ORG_ADMIN]
  const [inputValue, setInputValue] = useState('')
  const [validating, setValidating] = useState(false)
  const [validationResult, setValidationResult] = useState(null)
  const [validationError, setValidationError] = useState(null)
  const outcomeInputId = useId()
  const parsedInputValue = parseOutcomeId(inputValue)
  const canAddOutcome = parsedInputValue !== null && !outcomeIds.includes(parsedInputValue)

  // Fetch available offerings for the legacy single-org tool only; Command
  // Center already knows every destination's offerings and passes them as
  // `allOfferings`, so no org-scoped fetch happens in that mode.
  const { data: offeringsData } = useQuery({
    queryKey: ['kambi-offerings', orgSlug],
    queryFn: () => kambiApi.getOfferings(orgSlug),
    enabled: !isDestinationScoped && !!orgSlug,
  })

  const availableOfferings = useMemo(
    () => (isDestinationScoped ? (allOfferingsProp || EMPTY_OFFERINGS) : (offeringsData?.offering_ids || EMPTY_OFFERINGS)),
    [isDestinationScoped, allOfferingsProp, offeringsData?.offering_ids],
  )
  // availableOfferings still backs AvailabilitySummary's "x of y regulations"
  // read-out. Selecting regulations now happens in the composer's Publish step,
  // which also owns pruning any the creator is not allowed to use.

  const validate = useCallback(async (ids, offerings, destinationsArg, { throwOnError = false } = {}) => {
    if (ids.length === 0 || (isDestinationScoped && !(destinationsArg?.length > 0))) {
      setValidationResult(null)
      setValidationError(null)
      onValidationResult?.(null)
      return
    }
    setValidating(true)
    setValidationError(null)
    try {
      const result = isDestinationScoped
        ? await commandCenterApi.validateKambiOutcomesV2(providerId, ids, destinationsArg)
        : await kambiApi.validateOutcomes(orgSlug, ids, offerings.length > 0 ? offerings : undefined)
      setValidationResult(result)
      onValidationResult?.(result)
      return result
    } catch (err) {
      const failure = validationFailure(err)
      setValidationError(failure)
      setValidationResult(null)
      onValidationResult?.(null)
      if (throwOnError) throw err
      return null
    } finally {
      setValidating(false)
    }
  }, [isDestinationScoped, orgSlug, providerId, onValidationResult])

  useImperativeHandle(ref, () => ({
    revalidate: () => validate(outcomeIds, targetOfferings, destinations, { throwOnError: true }),
  }), [outcomeIds, targetOfferings, destinations, validate])

  // Re-validate whenever outcomeIds, targetOfferings, or destinations change
  const serializedIds = outcomeIds.join(',')
  const serializedOfferings = targetOfferings.join(',')
  const serializedDestinations = isDestinationScoped ? JSON.stringify(destinations) : ''
  useEffect(() => {
    validate(outcomeIds, targetOfferings, destinations)
  }, [serializedIds, serializedOfferings, serializedDestinations, validate]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddOutcome = () => {
    if (!canAddOutcome) return
    onOutcomeIdsChange([...outcomeIds, parsedInputValue])
    setInputValue('')
  }

  const handleRemoveOutcome = (id) => {
    onOutcomeIdsChange(outcomeIds.filter(oid => oid !== id))
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddOutcome()
    }
  }


  const outcomeChips = outcomeIds.length > 0 ? (
    <div className={styles.chipList} aria-label="Selected Kambi outcomes">
      {outcomeIds.map(id => (
        <span key={id} className={styles.chip}>
          {id}
          <button type="button" className={styles.chipRemove} onClick={() => handleRemoveOutcome(id)} aria-label={`Remove outcome ${id}`}>
            &times;
          </button>
        </span>
      ))}
    </div>
  ) : null

  return (
    <div className={styles.container}>
      {/* Regulations moved to the composer's Publish step; this component still
          reads targetOfferings to scope outcome validation. */}

      {/* Outcome ID Entry */}
      {!hideOutcomeEntry && (outcomeEntryDisclosure ? (
        <details className={styles.rawOutcomeDisclosure}>
          <summary>Use an outcome ID</summary>
          <div className={styles.section}>
            <label className={styles.sectionLabel} htmlFor={outcomeInputId}>Outcome ID</label>
            <div className={styles.inputRow}>
              <input
                id={outcomeInputId}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={20}
                className={styles.outcomeInput}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="4079532620"
              />
              <button type="button" className={styles.addBtn} onClick={handleAddOutcome} disabled={!canAddOutcome}>
                Add
              </button>
            </div>
            {outcomeChips}
          </div>
        </details>
      ) : (
        <div className={styles.section}>
          <label className={styles.sectionLabel} htmlFor={outcomeInputId}>Outcome ID</label>
          <div className={styles.inputRow}>
            <input
              id={outcomeInputId}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={20}
              className={styles.outcomeInput}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="4079532620"
            />
            <button type="button" className={styles.addBtn} onClick={handleAddOutcome} disabled={!canAddOutcome}>
              Add
            </button>
          </div>
        </div>
      ))}

      {!hideOutcomeEntry && !outcomeEntryDisclosure ? outcomeChips : null}

      {/* Loading indicator */}
      {validating && (
        <div className={styles.validatingMsg}>Checking bet…</div>
      )}

      {/* Validation state */}
      {validationError ? (
        <div
          className={`${styles.validationState} ${validationError.tone === 'notFound' ? styles.validationStateNotFound : styles.validationStateWarning}`}
          role="alert"
        >
          <strong>{validationError.title}</strong>
          <p>{validationError.description}</p>
          {showTechnicalDetails && validationError.technicalDetails ? (
            <details className={styles.validationTechnicalDetails}>
              <summary>Technical details</summary>
              <CodeBlock value={validationError.technicalDetails} label="Validation technical details" language="text" />
            </details>
          ) : null}
        </div>
      ) : null}

      {/* Warnings banner (e.g. cached/stale data) */}
      {validationResult?.warnings?.length > 0 && (
        <div className={styles.warningMsg}>
          {validationResult.warnings.map((w, i) => (
            <div key={i}>{w}</div>
          ))}
          {showTechnicalDetails && validationResult.warning_details?.length > 0 ? (
            <details className={styles.validationTechnicalDetails}>
              <summary>Technical details</summary>
              <CodeBlock
                value={validationResult.warning_details.join('\n')}
                label="Validation warning details"
                language="text"
              />
            </details>
          ) : null}
        </div>
      )}

      {/* Attached bet legs */}
      {!hideValidationPreview && validationResult?.outcomes && (
        <div className={styles.validationPreview} aria-label="Attached bet legs">
          {validationResult.events?.map((event) => {
            const eventOutcomes = Object.entries(validationResult.outcomes)
              .filter(([, data]) => data.event_name === event.name)
            return (
              <details key={event.id} className={styles.eventCard} aria-label={`Event ${event.name}`} open>
                <summary className={styles.eventHeader}>
                  <div className={styles.eventIdentity}>
                    <span className={styles.detailLabel}>Event</span>
                    <strong className={styles.eventName}>{event.name}</strong>
                  </div>
                  <div className={styles.eventMeta}>
                    {event.sport ? <span className={styles.eventSport}>{event.sport}</span> : null}
                    <span className={styles.eventCount}>
                      {eventOutcomes.length} {eventOutcomes.length === 1 ? 'outcome' : 'outcomes'}
                    </span>
                  </div>
                </summary>
                <div className={styles.eventOutcomes}>
                  {eventOutcomes.map(([outcomeId, data]) => {
                    const market = data.criterion || data.bet_offer_type || 'Market'
                    const betType = data.bet_offer_type && data.bet_offer_type !== market
                      ? data.bet_offer_type
                      : ''
                    return (
                      <details
                        key={outcomeId}
                        className={styles.outcomeRow}
                        aria-label={`Outcome ${data.outcome_id || outcomeId}`}
                      >
                        <summary className={styles.outcomeSummary}>
                          <div className={styles.outcomePrimary}>
                            <div className={styles.outcomeSelection}>
                              <span className={styles.detailLabel}>Selection</span>
                              <strong className={styles.outcomeName}>{data.selection_name || 'Selection'}</strong>
                              {data.participant && data.participant !== data.selection_name ? (
                                <span className={styles.participant}>{data.participant}</span>
                              ) : null}
                            </div>
                            <div className={styles.outcomePrice}>
                              <span className={styles.detailLabel}>Odds</span>
                              <strong className={styles.outcomeOdds}>{data.odds_american || 'Pending'}</strong>
                            </div>
                          </div>
                        </summary>
                        <div className={styles.outcomeBody}>
                          <dl className={styles.outcomeDetails}>
                            <div><dt>Market</dt><dd>{market}</dd></div>
                            {betType ? <div><dt>Bet type</dt><dd>{betType}</dd></div> : null}
                            {data.line ? <div><dt>Line</dt><dd>{data.line}</dd></div> : null}
                            {!outcomeEntryDisclosure ? <div><dt>Outcome ID</dt><dd>#{data.outcome_id}</dd></div> : null}
                          </dl>
                          <div className={styles.outcomeMeta} aria-label="Bet availability">
                            {data.outcome_status ? (
                              <span data-tone={data.outcome_status === 'OPEN' ? 'positive' : 'warning'}>
                                {formatStatusLabel(data.outcome_status.toLowerCase())}
                              </span>
                            ) : null}
                            {data.cash_out_status ? <span>Cash out {formatStatusLabel(data.cash_out_status).toLowerCase()}</span> : null}
                            {data.source === 'cached' ? <span>Cached price</span> : null}
                            <AvailabilitySummary
                              availability={data.availability}
                              targetOfferings={targetOfferings}
                              allOfferings={availableOfferings}
                            />
                          </div>
                        </div>
                      </details>
                    )
                  })}
                </div>
              </details>
            )
          })}
        </div>
      )}
    </div>
  )
})

export default KambiOutcomeSelector
